import { ReactComponent as BookIcon } from 'assets/icons/icon-book-open.svg';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import DangerButton from 'components/DangerButton/DangerButton';
import EditCurrencyBookExposureLimits from 'components/EditCurrencyBookExposureLimits/EditCurrencyBookExposureLimits';
import { useUpsertCurrencyBookLimitsMutation } from 'components/EditCurrencyBookExposureLimits/UpsertCurrencyBookExposureLimits.generated';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { makeMutationForm } from 'components/makeMutationForm/makeMutationForm';
import NoEntitiesMessage from 'components/NoEntitiesMessage/NoEntitiesMessage';
import Page from 'components/Page';
import PageIcon from 'components/PageIcon/PageIcon';
import ResetButton from 'components/ResetButton/ResetButton';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import Table from 'components/Table/Table';
import { curr } from 'helpers/curr';
import React, { useMemo } from 'react';
import { toast } from 'react-toastify';
import { routeMap, useNavigate } from 'routeMap';
import { useWrappedUrqlMutation } from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import HeroIconExclamation from 'toolbox/src/components/HeroIcons/HeroIconExclamation';
import PreventLeavingFormIfTouched from 'toolbox/src/components/PreventLeavingFormIfTouched/PreventLeavingFormIfTouched';
import Stack from 'toolbox/src/components/Stack/Stack';
import { currencyBookStrings } from './currencyBookStrings';
import { useDeleteCurrencyBookMutation } from './DeleteCurrencyBook.generated';
import { useGetCurrencyBookQuery } from './GetCurrencyBook.generated';
import {
  UpdateCurrencyBookMutationVariables,
  useUpdateCurrencyBookMutation,
} from './UpdateCurrencyBook.generated';
import { CurrencyBookLiquidityLimits } from './CurrencyBookLiquidityLimits/CurrencyBookLiquidityLimits';

const CurrencyBookForm = makeMutationForm<UpdateCurrencyBookMutationVariables>({
  config: {
    id: {
      type: 'hidden',
    },
    name: {
      type: 'text',
    },
    business_unit_id: {
      type: 'businessUnitId',
    },
    currency_id: {
      type: 'currencyId',
    },
  },
});

const CurrencyBook: React.FC<{
  id: number;
}> = ({ id }) => {
  const navigate = useNavigate();
  const [result] = useGetCurrencyBookQuery({ variables: { id } });

  const bank_accounts = result.data?.currency_books_by_pk?.bank_accounts || [];

  const banks = useMemo(() => {
    const banks = new Map<
      number,
      { id: number; name: string; image?: { public_url?: string } }
    >();

    /** Get unique banks */
    bank_accounts.forEach(({ bank }) => banks.set(bank.id, bank));

    return (
      Array.from(banks.values())
        /** Sort banks by name */
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [bank_accounts]);

  const update = useWrappedUrqlMutation(useUpdateCurrencyBookMutation, {
    onSuccess: () => toast.info('Updated currency book successfully.'),
  });

  const deleteBook = useWrappedUrqlMutation(useDeleteCurrencyBookMutation, {
    onSuccess: (result) => {
      toast.info('Deleted currency book successfully');
      navigate.viewBusinessUnit({
        params: {
          id: result.delete_currency_books.returning?.[0].business_unit_id,
        },
      });
    },
    onError: () => {},
  });

  const upsertExposureLimits = useWrappedUrqlMutation(
    useUpsertCurrencyBookLimitsMutation,
  );

  const currency_book = result.data?.currency_books_by_pk;

  if (!currency_book) {
    return null;
  }

  return (
    <Page
      icon={
        <Flex mx={1} alignItems="center">
          <div>
            <PageIcon Icon={BookIcon} />
          </div>
          <Breadcrumbs
            links={[
              {
                label: currency_book.business_unit.organisation.name,
                link: routeMap.viewOrgById({
                  params: {
                    id: currency_book.business_unit.organisation.id,
                  },
                }),
              },
              {
                label: currency_book.business_unit.name,
                link: routeMap.viewBusinessUnit({
                  params: {
                    id: currency_book.business_unit.id,
                  },
                }),
              },
              { label: currency_book.name },
            ]}
          />
        </Flex>
      }
    >
      <Card title="Manage Currency Book">
        <CurrencyBookForm.Wrapper
          initialValues={currency_book}
          onSubmit={(values) =>
            update.dispatch({
              id: values.id,
              name: values.name,
              business_unit_id: values.business_unit_id,
              currency_id: values.currency_id,
            })
          }
        >
          <CurrencyBookForm.Inputs.name label="Name" className="max-w-xs" />
          <CurrencyBookForm.Inputs.business_unit_id
            label="Business Unit"
            className="max-w-xs"
            initialOption={currency_book.business_unit}
          />
          <CurrencyBookForm.Inputs.currency_id
            label="Currency"
            className="max-w-xs"
            initialOption={currency_book.currency}
          />
          <Stack>
            <Flex justifyContent="flex-end" mx={2}>
              <ResetButton>{currencyBookStrings.resetButtonText}</ResetButton>
              <SubmitButton disableIfNoInputsTouched>
                {currencyBookStrings.submitButtonText}
              </SubmitButton>
            </Flex>
            <ErrorMessage {...update} />
          </Stack>
          <PreventLeavingFormIfTouched />
        </CurrencyBookForm.Wrapper>
      </Card>
      <Card
        title="Bank Accounts"
        footer={
          <Flex justifyContent="flex-end">
            <Button
              onClick={() =>
                navigate.createBankAccount({
                  search: {
                    orgId: currency_book.business_unit?.organisation?.id,
                    buId: currency_book?.business_unit_id,
                    cbId: currency_book?.id,
                    ccyId: currency_book?.currency_id,
                  },
                })
              }
            >
              New Bank Account
            </Button>
          </Flex>
        }
      >
        {!currency_book.bank_accounts.length && (
          <NoEntitiesMessage>
            {currencyBookStrings.noBankAccountsWarning(currency_book.name)}
          </NoEntitiesMessage>
        )}
        {currency_book.bank_accounts.length > 0 && (
          <Stack>
            <Flex justifyContent="flex-end" className="w-full">
              <div className="inline-block px-2 py-1 text-sm font-semibold rounded bg-jcap-100 text-jcap-700">
                {currency_book?.currency?.prefix} -{' '}
                {currency_book?.currency?.name}
              </div>
            </Flex>
            <Table
              columns={[
                { Header: 'Account', accessor: 'account_id' },
                {
                  Header: 'Bank',
                  accessor: ({ bank }) => bank?.name,
                },
                {
                  Header: 'Type',
                  accessor: ({
                    product_type,
                    fixed_maturity_date,
                    notice_days,
                  }) => (
                    <div className="text-xs">
                      <p>{product_type}</p>
                      <p>
                        {product_type === 'FIXED'
                          ? fixed_maturity_date
                          : product_type === 'NOTICE'
                          ? notice_days
                          : null}
                      </p>
                    </div>
                  ),
                },
                {
                  Header: 'Interest Rate',
                  accessor: ({ opening_interest_rate, actual_balances }) =>
                    `${actual_balances[0]?.interest_rate ||
                      opening_interest_rate}%`,
                },
                {
                  Header: 'Last Import',
                  accessor: ({ actual_balances }) => actual_balances[0]?.date,
                },
                {
                  Header: 'Balance',
                  accessor: ({ actual_balances }) =>
                    curr(
                      actual_balances[0]?.balance || 0,
                      currency_book.currency.prefix || null,
                    ),
                },
                {
                  Header: 'Actions',
                  accessor: ({ account_id }) => (
                    <button
                      onClick={() =>
                        navigate.viewBankAccountById({
                          params: {
                            account_id: account_id,
                          },
                        })
                      }
                      className="p-1 px-2 rounded bg-jcap-100 text-jcap-700"
                    >
                      View
                    </button>
                  ),
                },
              ]}
              data={currency_book?.bank_accounts}
              gridTemplateColumns="1.66fr 1.33fr repeat(4, 1fr) 100px"
            />
          </Stack>
        )}
      </Card>
      {banks.length > 0 && (
        <EditCurrencyBookExposureLimits
          banks={banks}
          exposureLimits={result.data.currency_books_by_pk.currency_book_bank_exposure_limits?.reduce(
            (obj, limit) => {
              return Object.assign(obj, {
                [limit.bank_id]: {
                  exposure_limit: limit.exposure_limit,
                },
              });
            },
            {},
          )}
          saveBankLimits={(bankLimits) => {
            return upsertExposureLimits.dispatch({
              currencyBookId: id,
              objects: bankLimits.map((bank) => {
                return {
                  bank_id: bank.id,
                  exposure_limit: bank.limit,
                  currency_book_id: id,
                };
              }),
            });
          }}
          onSuccess={() => toast('Updated exposure limits successfully')}
          onError={() => {
            toast.error('Something went wrong updating the exposure limits.');
          }}
        />
      )}

      {currency_book && <CurrencyBookLiquidityLimits currency_book_id={id} />}

      <Card title="Delete Currency Book">
        <p className="mb-4 text-sm text-gray-600">
          {currencyBookStrings.deleteCurrencyBookWarning}
        </p>
        <Stack alignItems="flex-end">
          <DangerButton
            onClick={() =>
              // eslint-disable-next-line no-restricted-globals
              confirm(
                'Are you sure you want to delete this currency book?\n\nAll bank accounts and balances associated with this currency book will also be deleted.',
              ) === true && deleteBook.dispatch({ id })
            }
          >
            <Flex mx={2}>
              <HeroIconExclamation />
              <p>{currencyBookStrings.deleteButtonText}</p>
            </Flex>
          </DangerButton>
          <ErrorMessage {...deleteBook} />
        </Stack>
      </Card>
    </Page>
  );
};

export default CurrencyBook;
